import { ApolloClient } from '@apollo/client';
import { LIMIT_POLICY_LIST_ITEM_QUERY } from './limit-policies.gql';
import type { LimitPoliciesQuery } from '@/graphql/generated';

export type LimitPolicyListNode = NonNullable<
	NonNullable<LimitPoliciesQuery['limitPolicies']>['nodes']
>[number];

interface LimitPolicyListItemResponse {
	limitPolicy?: LimitPolicyListNode | null;
}

export interface SelectLimitPolicyAfterSaveOptions {
	limitPolicyId: string;
	client: ApolloClient<any>;
	currentLimitPolicies: LimitPolicyListNode[];
	onDataUpdate: (updatedLimitPolicies: LimitPolicyListNode[]) => void;
	onLimitPolicySelect: (limitPolicy: LimitPolicyListNode) => void;
	onOpenDetailsPanel?: () => void;
}

/**
 * Fetches the latest limit policy data from the server and updates the list,
 * then selects and highlights the limit policy in the grid and details panel.
 * 
 * @param options Configuration options for selecting the limit policy
 * @returns Promise that resolves when the operation is complete
 */
export async function selectLimitPolicyAfterSave(
	options: SelectLimitPolicyAfterSaveOptions
): Promise<void> {
	const { 
		limitPolicyId, 
		client, 
		currentLimitPolicies, 
		onDataUpdate, 
		onLimitPolicySelect,
		onOpenDetailsPanel
	} = options;

	try {
		// Step 1: Fetch the latest limit policy data from the server
		const { data } = await client.query<LimitPolicyListItemResponse>({
			query: LIMIT_POLICY_LIST_ITEM_QUERY,
			variables: { id: limitPolicyId },
			fetchPolicy: 'network-only' // Always fetch fresh data from server
		});

		const fetchedLimitPolicy = data?.limitPolicy;

		if (!fetchedLimitPolicy) {
			console.warn(`Limit policy with ID ${limitPolicyId} not found after save`);
			return;
		}

		// Step 2: Check if limit policy exists in current list
		const existingLimitPolicyIndex = currentLimitPolicies.findIndex(
			p => p.limitPolicyId === limitPolicyId
		);

		let updatedLimitPolicies: LimitPolicyListNode[];

		if (existingLimitPolicyIndex >= 0) {
			// Limit policy exists - replace with updated data
			updatedLimitPolicies = [...currentLimitPolicies];
			updatedLimitPolicies[existingLimitPolicyIndex] = fetchedLimitPolicy;
		} else {
			// New limit policy - append to the list
			updatedLimitPolicies = [...currentLimitPolicies, fetchedLimitPolicy];
		}

		// Step 3: Update the list data
		onDataUpdate(updatedLimitPolicies);

		// Step 4: Select the limit policy (both in grid and details panel)
		// Use setTimeout to ensure the grid has been updated before selection
		setTimeout(() => {
			onLimitPolicySelect(fetchedLimitPolicy);
			if (onOpenDetailsPanel) {
				onOpenDetailsPanel();
			}
		}, 50);
	} catch (error) {
		console.error('Error selecting limit policy after save:', error);
		throw error;
	}
}
